import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import AppShell from './components/layout/AppShell';
import { useAuthStore } from './stores/authStore';
import { setReturnTo, peekReturnTo, clearReturnTo } from './utils/returnTo';
import { useAddons, useAddonsLoading } from './lib/addons';
import PermissionsLoader from './components/common/PermissionsLoader';
import RouteGuard from './components/common/RouteGuard';

const LoginPage    = lazy(() => import('./pages/Auth/LoginPage'));
const SuperadminEnter = lazy(() => import('./pages/Auth/SuperadminEnter'));
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const VehicleList  = lazy(() => import('./pages/Vehicles/VehicleListPage'));
const VehicleNew   = lazy(() => import('./pages/Vehicles/VehicleNewPage'));
const VehicleDetail= lazy(() => import('./pages/Vehicles/VehicleDetailPage'));
const VehicleCard  = lazy(() => import('./pages/Vehicles/VehicleSalesCardPage'));
const VehicleHandover = lazy(() => import('./pages/Vehicles/HandoverPage'));
const CustomerList = lazy(() => import('./pages/Customers/CustomerListPage'));
const InvoiceList   = lazy(() => import('./pages/Invoices/InvoiceListPage'));
const InvoiceNew    = lazy(() => import('./pages/Invoices/InvoiceNewPage'));
const InvoiceDetail = lazy(() => import('./pages/Invoices/InvoiceDetailPage'));
const InvoiceHistory = lazy(() => import('./pages/Invoices/InvoiceHistoryPage'));
const PokladnaLayout    = lazy(() => import('./pages/Pokladna/PokladnaLayout'));
const CashDocumentsPage = lazy(() => import('./pages/Pokladna/CashDocumentsPage'));
const PokladnaOverviewPage = lazy(() => import('./pages/Pokladna/PokladnaOverviewPage'));
const CashBookPage      = lazy(() => import('./pages/Pokladna/CashBookPage'));
const BankPage          = lazy(() => import('./pages/Pokladna/BankPage'));
const AccountingExportPage = lazy(() => import('./pages/Pokladna/AccountingExportPage'));
const Documents    = lazy(() => import('./pages/Documents/DocumentsPage'));
const Advertising  = lazy(() => import('./pages/Advertising/AdvertisingPage'));
const Settings     = lazy(() => import('./pages/Settings/SettingsPage'));
const ManualPage   = lazy(() => import('./pages/Manual/ManualPage'));
const SalesPage    = lazy(() => import('./pages/Sales/SalesPage'));
const PurchasePage = lazy(() => import('./pages/Purchase/PurchaseWizardPage'));
const Statistics   = lazy(() => import('./pages/Statistics/StatisticsPage'));
const InvestmentPage = lazy(() => import('./pages/Investments/InvestmentPage'));
const InventoryLayout      = lazy(() => import('./pages/Inventory/InventoryLayout'));
const StockItemsPage       = lazy(() => import('./pages/Inventory/StockItemsPage'));
const InventoryListPage    = lazy(() => import('./pages/Inventory/InventoryListPage'));
const InventoryDetailPage  = lazy(() => import('./pages/Inventory/InventoryDetailPage'));
const WarehouseVehiclesPage = lazy(() => import('./pages/Inventory/WarehouseVehiclesPage'));
const VehicleInventoryListPage   = lazy(() => import('./pages/Inventory/VehicleInventoryListPage'));
const VehicleInventoryDetailPage = lazy(() => import('./pages/Inventory/VehicleInventoryDetailPage'));
const MovementsPage              = lazy(() => import('./pages/Inventory/MovementsPage'));
const ServiceLayout             = lazy(() => import('./pages/Service/ServiceLayout'));
const ServiceOverviewPage       = lazy(() => import('./pages/Service/ServiceOverviewPage'));
const ServiceOrdersPage         = lazy(() => import('./pages/Service/ServiceOrdersPage'));
const ServiceOrderDetailPage    = lazy(() => import('./pages/Service/ServiceOrderDetailPage'));
const ServiceCatalogPage        = lazy(() => import('./pages/Service/ServiceCatalogPage'));
const ServicePartsOrdersPage    = lazy(() => import('./pages/Service/PartsOrdersPage'));
const ServicePartsOrderDetail   = lazy(() => import('./pages/Service/PartsOrderDetailPage'));
const ServiceClaimsPage         = lazy(() => import('./pages/Service/ClaimsPage'));
const ServiceRemindersPage      = lazy(() => import('./pages/Service/RemindersPage'));
const AftersalesPage            = lazy(() => import('./pages/Aftersales/AftersalesPage'));
const MobileApp                 = lazy(() => import('./pages/Mobile/MobileApp'));

function LoadingFallback() {
  return <div className="flex h-full items-center justify-center py-20"><div className="text-gray-400 text-sm">Načítám…</div></div>;
}

/** Nepřihlášený uživatel jde na přihlášení; adresu (např. z QR kódu /m/v/…) si pamatujeme a po přihlášení se na ni vrátí. */
function ToLogin() {
  const loc = useLocation();
  setReturnTo(loc.pathname + loc.search);
  return <Navigate to="/login" replace />;
}

/** Po přihlášení na telefonu (modul Mobil) otevře mobilní rozhraní, pokud si uživatel nevybral plnou verzi. */
function PhoneRedirect() {
  const mobile = useAddons().mobile;
  const loading = useAddonsLoading();
  if (loading) return <LoadingFallback />;
  let full = false;
  try { full = sessionStorage.getItem('m_full') === '1'; } catch { /* bez úložiště se řídíme jen šířkou */ }
  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  return mobile && phone && !full ? <Navigate to="/m" replace /> : <Navigate to="/dashboard" replace />;
}

function ProtectedApp() {
  return (
    <AppShell>
      <PermissionsLoader />
      <Suspense fallback={<LoadingFallback />}>
       <RouteGuard>
        <Routes>
          <Route path="/"                element={<PhoneRedirect />} />
          <Route path="/dashboard"       element={<Dashboard />} />
          <Route path="/vehicles"        element={<VehicleList />} />
          <Route path="/vehicles/new"       element={<VehicleNew />} />
          <Route path="/vehicles/:id/edit" element={<VehicleNew />} />
          <Route path="/vehicles/:id/card"     element={<VehicleCard />} />
          <Route path="/vehicles/:id/handover" element={<VehicleHandover />} />
          <Route path="/vehicles/:id"          element={<VehicleDetail />} />
          <Route path="/customers"       element={<CustomerList />} />
          <Route path="/aftersales"      element={<AftersalesPage />} />
          {/* Pokladna – faktury + hotovostní doklady */}
          <Route path="/pokladna"        element={<PokladnaLayout />}>
            <Route index                  element={<Navigate to="/pokladna/faktury" replace />} />
            <Route path="prehled"         element={<PokladnaOverviewPage />} />
            <Route path="kasa"            element={<CashBookPage />} />
            <Route path="banka"           element={<BankPage />} />
            <Route path="export"          element={<AccountingExportPage />} />
            <Route path="faktury"         element={<InvoiceList />} />
            <Route path="historie"        element={<InvoiceHistory />} />
            <Route path="pokladni-doklady" element={<CashDocumentsPage />} />
          </Route>
          {/* Zpětná kompatibilita starých odkazů */}
          <Route path="/invoices"        element={<Navigate to="/pokladna/faktury" replace />} />
          <Route path="/invoices/new"    element={<InvoiceNew />} />
          <Route path="/invoices/:id"    element={<InvoiceDetail />} />
          <Route path="/documents"       element={<Navigate to="/settings" replace />} />
          <Route path="/sales"           element={<SalesPage />} />
          <Route path="/vykup"           element={<PurchasePage />} />
          <Route path="/advertising"     element={<Advertising />} />
          <Route path="/statistics"      element={<Statistics />} />
          <Route path="/investments"     element={<InvestmentPage />} />
          <Route path="/settings"        element={<Settings />} />
          <Route path="/manual"          element={<ManualPage />} />
          <Route path="/inventory"       element={<InventoryLayout />}>
            <Route index                 element={<Navigate to="/inventory/stock-items" replace />} />
            <Route path="stock-items"    element={<StockItemsPage />} />
            <Route path="vehicles"       element={<WarehouseVehiclesPage />} />
            <Route path="sessions"       element={<InventoryListPage />} />
            <Route path="sessions/:id"   element={<InventoryDetailPage />} />
            <Route path="vehicle-inventory"      element={<VehicleInventoryListPage />} />
            <Route path="vehicle-inventory/:id"  element={<VehicleInventoryDetailPage />} />
            <Route path="movements"              element={<MovementsPage />} />
          </Route>
          {/* Servis aut – layout sám hlídá, jestli je addon aktivní */}
          <Route path="/service"         element={<ServiceLayout />}>
            <Route index                 element={<ServiceOverviewPage />} />
            <Route path="orders"         element={<ServiceOrdersPage />} />
            <Route path="orders/:id"     element={<ServiceOrderDetailPage />} />
            <Route path="catalog"        element={<ServiceCatalogPage />} />
            <Route path="parts-orders"     element={<ServicePartsOrdersPage />} />
            <Route path="parts-orders/:id" element={<ServicePartsOrderDetail />} />
            <Route path="claims"         element={<ServiceClaimsPage />} />
            <Route path="reminders"      element={<ServiceRemindersPage />} />
            <Route path="*"              element={<Navigate to="/service" replace />} />
          </Route>
          <Route path="*"                element={<Navigate to="/dashboard" replace />} />
        </Routes>
       </RouteGuard>
      </Suspense>
    </AppShell>
  );
}

export default function App() {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn);
  const loc = useLocation();
  // Zapamatovaný návrat po přihlášení platí jen jednou.
  useEffect(() => { if (isLoggedIn && !loc.pathname.startsWith('/login')) clearReturnTo(); }, [isLoggedIn, loc.pathname]);

  return (
    <Suspense fallback={<div className="flex h-screen items-center justify-center text-gray-400">Načítám…</div>}>
      <Routes>
        <Route path="/login" element={isLoggedIn ? <Navigate to={peekReturnTo() ?? '/'} replace/> : <LoginPage/>}/>
        <Route path="/sa-login" element={<SuperadminEnter/>}/>
        <Route path="/m/*"   element={isLoggedIn ? <MobileApp/> : <ToLogin/>}/>
        <Route path="/*"     element={isLoggedIn ? <ProtectedApp/> : <ToLogin/>}/>
      </Routes>
    </Suspense>
  );
}
